import {
  AcademicCapIcon,
  AdjustmentsHorizontalIcon,
  ArchiveBoxIcon,
  ArrowUpTrayIcon,
  BanknotesIcon,
  BuildingOffice2Icon,
  ChartPieIcon,
  ChatBubbleLeftEllipsisIcon,
  BuildingLibraryIcon,
  CheckBadgeIcon,
  ClipboardDocumentCheckIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  HomeIcon,
  ListBulletIcon,
  PencilSquareIcon,
  PresentationChartBarIcon,
  UserGroupIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { ROLES } from "../auth/roles";
import { hasAnyRole } from "../utils/auth";

/*
 * The sidebar menu, in one place. Each entry lists the roles that may see it, matching the
 * page's role list in App.js (UI only; the backend checks every request). `matches` holds
 * extra path prefixes that keep the entry highlighted on its detail pages. Submenu entries carry a
 * short `description` shown under their label.
 */
const NAVIGATION = [
  { id: "student-portal", label: "Scholarships", href: "/studentPortal", Icon: HomeIcon, roles: [ROLES.APPLICANT], matches: ["/scholarships/"] },
  { id: "student-applications", label: "My Applications", href: "/my-applications", Icon: ClipboardDocumentCheckIcon, roles: [ROLES.APPLICANT], matches: ["/my-applications/"] },
  { id: "student-loan", label: "Local Loan", href: "/student/localLoan", Icon: BanknotesIcon, roles: [ROLES.APPLICANT] },
  { id: "student-profile", label: "Academic Profile", href: "/academic-profile", Icon: AcademicCapIcon, roles: [ROLES.APPLICANT] },
  { id: "dashboard", label: "Dashboard", href: "/dashboard", Icon: ChartPieIcon, roles: [ROLES.ADMIN] },
  {
    id: "marking",
    label: "Marking",
    Icon: ClipboardDocumentCheckIcon,
    roles: [ROLES.EVALUATOR],
    children: [
      { label: "To mark", href: "/evaluation/to-mark", description: "Applications waiting for your marks", Icon: PencilSquareIcon, roles: [ROLES.EVALUATOR] },
      { label: "All marked", href: "/evaluation/marked", description: "Waiting for HEC's decision", Icon: CheckBadgeIcon, roles: [ROLES.EVALUATOR] },
      { label: "Finished", href: "/evaluation/finished", description: "Decided or cancelled calls", Icon: ArchiveBoxIcon, roles: [ROLES.EVALUATOR] },
    ],
  },
  { id: "payments", label: "Loan Payments", href: "/loan-payments", Icon: BanknotesIcon, roles: [ROLES.BRD], matches: ["/loan-payments/"] },
  { id: "brd-private-lists", label: "Private Institution Lists", href: "/brd/private-lists", Icon: BuildingLibraryIcon, roles: [ROLES.BRD], matches: ["/brd/private-lists/"] },
  { id: "partner", label: "Scholarships", href: "/partner", Icon: UserGroupIcon, roles: [ROLES.SCHOLARSHIP_OWNER], matches: ["/partner/"] },
  {
    id: "scholarships",
    label: "Scholarships",
    Icon: GlobeAltIcon,
    roles: [ROLES.ADMIN, ROLES.DG, ROLES.MINISTER],
    children: [
      { label: "Scholarships", href: "/scholarshipList", description: "Partner scholarships", Icon: ListBulletIcon, roles: [ROLES.ADMIN, ROLES.DG, ROLES.MINISTER] },
      { label: "Applicants", href: "/applicantsList", description: "Review and nominate", Icon: UsersIcon, roles: [ROLES.ADMIN, ROLES.DG, ROLES.MINISTER], matches: ["/applicantsList/"] },
      { label: "Nominations", href: "/nominations", description: "Nominees and the partner's answers", Icon: CheckBadgeIcon, roles: [ROLES.ADMIN, ROLES.DG], matches: ["/nominations/"] },
      { label: "Partners", href: "/sponsors", description: "Organisations funding scholarships", Icon: BuildingOffice2Icon, roles: [ROLES.ADMIN, ROLES.DG, ROLES.MINISTER] },
    ],
  },
  {
    id: "abroad-loans",
    label: "Abroad Loans",
    Icon: BanknotesIcon,
    roles: [ROLES.ADMIN, ROLES.DG, ROLES.MINISTER],
    children: [
      { label: "Loan calls", href: "/loans/abroad", description: "Loans to study abroad", Icon: ListBulletIcon, roles: [ROLES.ADMIN, ROLES.DG, ROLES.MINISTER] },
      { label: "Programmes", href: "/loans/abroad/programmes", description: "Levels and programmes calls offer", Icon: AcademicCapIcon, roles: [ROLES.ADMIN, ROLES.DG, ROLES.MINISTER] },
      { label: "Applicants", href: "/loans/abroad/applicants", description: "Review and award", Icon: UsersIcon, roles: [ROLES.ADMIN, ROLES.DG, ROLES.MINISTER], matches: ["/loans/abroad/applicants/"] },
      { label: "Approvals", href: "/loans/abroad/approvals", description: "The DG's and the Minister's decisions", Icon: CheckBadgeIcon, roles: [ROLES.ADMIN, ROLES.DG, ROLES.MINISTER], matches: ["/loans/abroad/approvals/"] },
    ],
  },
  {
    id: "private-loans",
    label: "Private Institution Loans",
    Icon: BuildingLibraryIcon,
    roles: [ROLES.ADMIN, ROLES.DG, ROLES.MINISTER],
    children: [
      { label: "Institution lists", href: "/loans/private", description: "Lists private institutions send for approval", Icon: ListBulletIcon, roles: [ROLES.ADMIN, ROLES.DG, ROLES.MINISTER], matches: ["/loans/private/"] },
    ],
  },
  {
    id: "local",
    label: "Bursaries",
    Icon: AcademicCapIcon,
    roles: [ROLES.ADMIN],
    children: [
      { label: "Institutions", href: "/bursaries/institutions", description: "Requests and students by institution", Icon: BuildingLibraryIcon, roles: [ROLES.ADMIN], matches: ["/bursaries/institutions/"] },
      { label: "Selection Settings", href: "/selection", description: "Set and run selections", Icon: AdjustmentsHorizontalIcon, roles: [ROLES.ADMIN] },
      { label: "Loan Approval", href: "/loanApprovalStep", description: "Verify and approve selections", Icon: ClipboardDocumentCheckIcon, roles: [ROLES.ADMIN] },
      { label: "Institution Uploads", href: "/local-loan-results", description: "Admitted students each institution uploaded", Icon: ArrowUpTrayIcon, roles: [ROLES.ADMIN] },
      { label: "Granted Students", href: "/GrantedStudents", description: "View granted students", Icon: UserGroupIcon, roles: [ROLES.ADMIN] },
      { label: "Student Claims", href: "/studentClaims", description: "Review rejection claims", Icon: ChatBubbleLeftEllipsisIcon, roles: [ROLES.ADMIN] },
    ],
  },
  {
    id: "hli-private",
    label: "Private Institution Loans",
    Icon: BuildingLibraryIcon,
    roles: [ROLES.HLI_ADMIN],
    children: [
      { label: "Loan lists", href: "/hli/lists", description: "Lists of your students sent to HEC", Icon: ListBulletIcon, roles: [ROLES.HLI_ADMIN], matches: ["/hli/lists/"] },
    ],
  },
  {
    id: "hli-local",
    label: "Bursaries",
    Icon: AcademicCapIcon,
    roles: [ROLES.HLI_ADMIN],
    children: [
      { label: "Admitted Students", href: "/hli/bursaryApplications", description: "Students your institution uploaded", Icon: DocumentTextIcon, roles: [ROLES.HLI_ADMIN] },
      { label: "Loan Approval", href: "/hliAdmin", description: "Grant or reject loan requests", Icon: ClipboardDocumentCheckIcon, roles: [ROLES.HLI_ADMIN] },
      { label: "Selection Settings", href: "/hli/selection", description: "Request window and selections", Icon: AdjustmentsHorizontalIcon, roles: [ROLES.HLI_ADMIN] },
      { label: "Upload Students", href: "/hli/uploadStudents", description: "Your admitted students", Icon: ArrowUpTrayIcon, roles: [ROLES.HLI_ADMIN] },
      { label: "Granted Students", href: "/hli/grantedStudents", description: "View granted students", Icon: UserGroupIcon, roles: [ROLES.HLI_ADMIN] },
      { label: "Student Claims", href: "/hli/studentClaims", description: "Review rejection claims", Icon: ChatBubbleLeftEllipsisIcon, roles: [ROLES.HLI_ADMIN] },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    Icon: PresentationChartBarIcon,
    roles: [ROLES.ADMIN, ROLES.DG, ROLES.MINISTER],
    children: [
      { label: "Scholarships and Loans", href: "/reports/scholarships", description: "Applications, awards and follow-up", Icon: AcademicCapIcon, roles: [ROLES.ADMIN, ROLES.DG, ROLES.MINISTER] },
    ],
  },
  { id: "settings", label: "Settings", href: "/settings", Icon: AdjustmentsHorizontalIcon, roles: [ROLES.ADMIN] },
  {
    id: "admin",
    label: "Administration",
    Icon: Cog6ToothIcon,
    roles: [ROLES.ADMIN],
    children: [{ label: "Manage Users", href: "/admin/users", description: "Manage users and institutions", Icon: UsersIcon, roles: [ROLES.ADMIN] }],
  },
];

// The menu entries the signed-in user may see; groups left with no children are dropped
export const visibleNavigation = () =>
  NAVIGATION.filter((item) => hasAnyRole(...item.roles))
    .map((item) => (item.children ? { ...item, children: item.children.filter((c) => hasAnyRole(...c.roles)) } : item))
    .filter((item) => !item.children || item.children.length > 0);

export const isActiveLink = (item, pathname) =>
  item.href === pathname || (item.matches ?? []).some((prefix) => pathname.startsWith(prefix));
